import { useEffect } from 'react';
import { useI18n } from '../context/I18nContext';

// Languages served by the backend — kept in sync with backend SUPPORTED_LANGS.
// Used to emit hreflang alternate link tags per route.
const SUPPORTED_LANGS = [
  'en', 'es', 'fr', 'fr-CA', 'pt-BR', 'de', 'ja', 'zh-CN',
  'ar', 'th', 'vi', 'id', 'nl', 'he',
];

// data-seo attribute used so we can safely clear previously-injected tags
// without touching static tags defined in public/index.html.
const SEO_ATTR = 'data-seo';

function ensureMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function setLinkRel({ rel, href, hreflang }) {
  const link = document.createElement('link');
  link.setAttribute(SEO_ATTR, 'true');
  link.setAttribute('rel', rel);
  link.setAttribute('href', href);
  if (hreflang) link.setAttribute('hreflang', hreflang);
  document.head.appendChild(link);
}

function injectJsonLd(payload) {
  // Injects a single <script type="application/ld+json"> tag tagged with
  // data-seo so the regular SEO clear-cycle removes it on the next route
  // change. Accepts either an object or an array (Google supports both).
  const script = document.createElement('script');
  script.setAttribute(SEO_ATTR, 'true');
  script.type = 'application/ld+json';
  try {
    script.text = JSON.stringify(payload);
  } catch {
    return; // bail silently on circular refs — never break the page over SEO
  }
  document.head.appendChild(script);
}

function clearSeoManagedTags() {
  document.head.querySelectorAll(`[${SEO_ATTR}="true"]`).forEach((n) => n.remove());
}

/**
 * SEO head manager — dynamically sets <title>, meta description, OG tags,
 * canonical URL, and hreflang alternates per route + active language.
 *
 * Pulls localized title/description from the active i18n bundle under
 * `t.seo[pageKey]` when `pageKey` is provided. Props `title`/`description`
 * serve as fallbacks.
 */
export default function SeoHead({ pageKey, title, description, path = '/', image, jsonLd }) {
  const { lang, t } = useI18n();
  const seoBundle = pageKey && t && t.seo && t.seo[pageKey] ? t.seo[pageKey] : null;
  const effectiveTitle = (seoBundle && seoBundle.title) || title;
  const effectiveDescription = (seoBundle && seoBundle.description) || description;
  const effectiveImage = (seoBundle && seoBundle.og_image) || image;

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
    const currentParams = new URLSearchParams(currentSearch);
    const canonicalUrl = `${origin}${path}${lang !== 'en' ? `?lang=${encodeURIComponent(lang)}` : ''}`;

    // <title>
    if (effectiveTitle) document.title = effectiveTitle;

    // Clear previously injected SEO tags (not static ones from index.html)
    clearSeoManagedTags();

    // Description + OG + Twitter
    if (effectiveDescription) {
      ensureMeta('meta[name="description"]', { name: 'description', content: effectiveDescription });
      ensureMeta('meta[property="og:description"]', { property: 'og:description', content: effectiveDescription });
      ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: effectiveDescription });
    }
    if (effectiveTitle) {
      ensureMeta('meta[property="og:title"]', { property: 'og:title', content: effectiveTitle });
      ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: effectiveTitle });
    }
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    if (effectiveImage) {
      ensureMeta('meta[property="og:image"]', { property: 'og:image', content: effectiveImage });
      ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: effectiveImage });
    }
    ensureMeta('meta[property="og:locale"]', { property: 'og:locale', content: lang.replace('-', '_') });
    ensureMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' });

    // Google Search Console verification — set REACT_APP_GSC_VERIFICATION in
    // frontend/.env to the value Google gives you on the "HTML tag" verification
    // step (the bit after `content="…"`). Empty string means no tag is rendered.
    const gscToken = process.env.REACT_APP_GSC_VERIFICATION;
    if (gscToken && gscToken.trim()) {
      ensureMeta('meta[name="google-site-verification"]', { name: 'google-site-verification', content: gscToken.trim() });
    }

    // Canonical
    setLinkRel({ rel: 'canonical', href: canonicalUrl });

    // hreflang alternates per supported language
    SUPPORTED_LANGS.forEach((code) => {
      const href = `${origin}${path}${code === 'en' ? '' : `?lang=${encodeURIComponent(code)}`}`;
      setLinkRel({ rel: 'alternate', href, hreflang: code });
    });
    // x-default points to English canonical
    setLinkRel({ rel: 'alternate', href: `${origin}${path}`, hreflang: 'x-default' });

    // Optional schema.org JSON-LD payload (Organization, WebPage, etc.)
    if (jsonLd) injectJsonLd(jsonLd);

    // Preserve params other than lang when the language changes (no-op here;
    // kept so future filtered listings don't lose state)
    currentParams.get('lang'); // suppress unused warning in some eslint configs

    return () => {
      // leave tags in place — they'll be overwritten by the next SeoHead
    };
  }, [effectiveTitle, effectiveDescription, path, lang, effectiveImage, jsonLd]);

  return null;
}
